import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {CodonTranslationTable, GENETIC_CODE_USER_PREFERENECE_KEY} from 'biojs/BioConstants';
import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {BulkEditTranslationRegionGroupData, TranslationRegionModificationType} from 'molbio/dao/SequenceDAO';
import BulkEditModal from 'molbio/sequence-features/BulkEditModal';
import PopoverColorPicker from 'molbio/sequence-features/PopoverColorPicker';
import StrandDropdown from 'molbio/sequence-features/StrandDropdown';
import CoerceStartCodonToMethionineDropdown from 'molbio/translations/CoerceStartCodonToMethionineDropdown';
import GeneticCodeDropdown from 'molbio/translations/GeneticCodeDropdown';
import {
  getTranslationNameOrPlaceholder,
  TranslationRegionGroupData,
} from 'molbio/translations/TranslationUtil';
import FormGroup from 'ui-library/bs/FormGroup';
import usePreference from 'ui-library/hooks/usePreference';
import FieldLabel from 'ui-library/inputs/FieldLabel';

interface FormValueProps<T> {
  value: T;
  setValue: (value: T) => void;
  allSameValue: boolean;
}

interface TranslationBulkEditModalContentProps {
  isSingleStranded: boolean;
  geneticCodeProps: FormValueProps<CodonTranslationTable | null>;
  strandProps: FormValueProps<StrandOfDoubleStranded | null>;
  colorProps: FormValueProps<string | null>;
  coerceStartCodonToMethionineProps: FormValueProps<boolean | null>;
}

function TranslationBulkEditModalContent({
  isSingleStranded,
  geneticCodeProps,
  strandProps,
  colorProps,
  coerceStartCodonToMethionineProps,
}: TranslationBulkEditModalContentProps): JSX.Element {
  return (
    <>
      <div className={css(styles.modalContentRow)}>
        <FormGroup className={css(styles.fieldSection)}>
          <FieldLabel className={css(styles.fieldLabel)} label="Genetic code" />
          <GeneticCodeDropdown
            geneticCode={geneticCodeProps.value}
            onSelectGeneticCode={geneticCodeProps.setValue}
            includeNullOption={!geneticCodeProps.allSameValue}
          />
        </FormGroup>
        <FormGroup className={css(styles.fieldSection)}>
          <FieldLabel className={css(styles.fieldLabel)} label="Strand" />
          <StrandDropdown
            strand={strandProps.value}
            onSelectStrand={strandProps.setValue}
            allowNone={false}
            includeNullOption={!strandProps.allSameValue}
            disabled={isSingleStranded}
          />
        </FormGroup>
      </div>
      <div className={css(styles.modalContentRow)}>
        <FormGroup className={css(styles.fieldSection)}>
          <FieldLabel className={css(styles.fieldLabel)} label="First codon" />
          <CoerceStartCodonToMethionineDropdown
            coerceStartCodonToMethionine={coerceStartCodonToMethionineProps.value}
            onChangeCoerceStartCodonToMethionine={coerceStartCodonToMethionineProps.setValue}
            includeNullOption={!coerceStartCodonToMethionineProps.allSameValue}
          />
        </FormGroup>
        <FormGroup className={css(styles.fieldSection)}>
          <FieldLabel className={css(styles.fieldLabel)} label="Color" />
          <PopoverColorPicker
            selectedColor={colorProps.value}
            onSelectColor={colorProps.setValue}
            asDropdown={true}
          />
        </FormGroup>
      </div>
    </>
  );
}

export interface TranslationBulkEditModalProps {
  selectedTranslationRegionGroups: Array<TranslationRegionGroupData>;
  sequenceStartIndex: number;
  sequenceLength: number;
  isSingleStranded: boolean;
  bulkEditTranslations: (data: Array<BulkEditTranslationRegionGroupData>) => void;
  onRequestClose: () => void;
}

/**
 * Modal to bulk edit translations
 */
function TranslationBulkEditModal({
  selectedTranslationRegionGroups,
  sequenceStartIndex,
  sequenceLength,
  isSingleStranded,
  bulkEditTranslations,
  onRequestClose,
}: TranslationBulkEditModalProps): JSX.Element {
  const [_, setGeneticCodeUserPreference] = usePreference<CodonTranslationTable>(
    GENETIC_CODE_USER_PREFERENECE_KEY
  );
  const allSameStrand = getAllSameAttribute('strand');
  const [strand, setStrand] = useState(allSameStrand ? selectedTranslationRegionGroups[0].strand : null);
  const allSameGeneticCode = getAllSameAttribute('codonTranslationTable');
  const [geneticCode, setGeneticCode] = useState(
    allSameGeneticCode ? selectedTranslationRegionGroups[0].codonTranslationTable : null
  );
  const allSameColor = getAllSameAttribute('color');
  const [color, setColor] = useState(allSameColor ? selectedTranslationRegionGroups[0].color : null);
  const allSameCoerceStartCodonToMethionine = getAllSameAttribute('coerceStartCodonToMethionine');
  const [coerceStartCodonToMethionine, setCoerceStartCodonToMethionine] = useState(
    allSameCoerceStartCodonToMethionine
      ? selectedTranslationRegionGroups[0].coerceStartCodonToMethionine
      : null
  );

  function getAllSameAttribute(key: keyof TranslationRegionGroupData): boolean {
    return selectedTranslationRegionGroups.every(
      (group) => group[key] === selectedTranslationRegionGroups[0][key]
    );
  }

  function onClickSave(): void {
    const changedStrand =
      strand != null && !(allSameStrand && strand === selectedTranslationRegionGroups[0].strand);
    const changedGeneticCode =
      geneticCode != null &&
      !(allSameGeneticCode && geneticCode === selectedTranslationRegionGroups[0].codonTranslationTable);
    const changedColor =
      color != null && !(allSameColor && color === selectedTranslationRegionGroups[0].color);
    const changedCoerceStartCodonToMethionine =
      coerceStartCodonToMethionine != null &&
      !(
        allSameCoerceStartCodonToMethionine &&
        coerceStartCodonToMethionine === selectedTranslationRegionGroups[0].coerceStartCodonToMethionine
      );
    if (changedStrand || changedGeneticCode || changedColor || changedCoerceStartCodonToMethionine) {
      const data: Array<BulkEditTranslationRegionGroupData> = selectedTranslationRegionGroups.map(
        ({hasGroup, translations, cid}) => ({
          id: hasGroup ? parseInt(cid, 10) : null,
          // Must define region modifications when editing a lone translation, even if strand hasn't changed
          regions:
            changedStrand || !hasGroup
              ? translations.map(({id}) => ({
                  modificationType: TranslationRegionModificationType.UPDATE,
                  id,
                  strand: changedStrand ? strand : undefined,
                }))
              : [],
          properties:
            changedGeneticCode || changedColor || changedCoerceStartCodonToMethionine
              ? {
                  codonTranslationTable: geneticCode ?? undefined,
                  color: color ?? undefined,
                  coerceStartCodonToMethionine: coerceStartCodonToMethionine ?? undefined,
                }
              : undefined,
        })
      );
      if (changedGeneticCode) {
        setGeneticCodeUserPreference(geneticCode);
      }
      bulkEditTranslations(data);
    }
    onRequestClose();
  }

  return (
    <BulkEditModal
      onRequestClose={onRequestClose}
      collectionName="translation"
      featureNames={selectedTranslationRegionGroups.map((translation) =>
        getTranslationNameOrPlaceholder(translation, sequenceStartIndex, sequenceLength)
      )}
      onClickSave={onClickSave}
    >
      <TranslationBulkEditModalContent
        isSingleStranded={isSingleStranded}
        geneticCodeProps={{value: geneticCode, setValue: setGeneticCode, allSameValue: allSameGeneticCode}}
        strandProps={{value: strand, setValue: setStrand, allSameValue: allSameStrand}}
        colorProps={{value: color, setValue: setColor, allSameValue: allSameColor}}
        coerceStartCodonToMethionineProps={{
          value: coerceStartCodonToMethionine,
          setValue: setCoerceStartCodonToMethionine,
          allSameValue: allSameCoerceStartCodonToMethionine,
        }}
      />
    </BulkEditModal>
  );
}

const styles = StyleSheet.create({
  modalContentRow: {
    display: 'flex',
    gap: '16px',
  },
  fieldSection: {
    flex: 1,
  },
  fieldLabel: {
    display: 'block',
  },
});

export default TranslationBulkEditModal;
